import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import SaasLayout from "./SaasLayout.jsx";
import { establishedProfessionIds, professionCatalogue } from "../new-hub/professions/catalogue.js";

const lazyNamed = (load, exportName) =>
  lazy(() => load().then((module) => ({ default: module[exportName] })));

const OptimizedNewHubApp = lazy(() => import("../new-hub/OptimizedApp"));
const MarketingLayout = lazy(() => import("./MarketingLayout.jsx"));
const BlogIndexPage = lazyNamed(() => import("./pages/BlogIndexPage"), "BlogIndexPage");
const BlogArticlePage = lazyNamed(() => import("./pages/BlogArticlePage"), "BlogArticlePage");
const TemplatesPage = lazyNamed(() => import("./pages/TemplatesPage"), "TemplatesPage");
const WhatIsOfflicoPage = lazyNamed(() => import("./pages/WhatIsOfflicoPage"), "WhatIsOfflicoPage");
const ToolsHubPage = lazyNamed(() => import("./pages/ToolsPages"), "ToolsHubPage");
const ServicePricingCalculatorPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "ServicePricingCalculatorPage",
);
const NoShowCostCalculatorPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "NoShowCostCalculatorPage",
);
const MileageClaimCalculatorPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "MileageClaimCalculatorPage",
);
const DailyRouteCapacityCalculatorPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "DailyRouteCapacityCalculatorPage",
);
const LateCancellationPolicyBuilderPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "LateCancellationPolicyBuilderPage",
);
const AppointmentReminderMessageGeneratorPage = lazyNamed(
  () => import("./pages/ToolsPages"),
  "AppointmentReminderMessageGeneratorPage",
);
const GdprDocumentChecklistPage = lazyNamed(
  () => import("./pages/templates/GdprDocumentChecklistPage"),
  "GdprDocumentChecklistPage",
);
const AppointmentConfirmationTemplatePage = lazyNamed(
  () => import("./pages/templates/AppointmentConfirmationTemplatePage"),
  "AppointmentConfirmationTemplatePage",
);
const CancellationPolicyTemplatePage = lazyNamed(
  () => import("./pages/templates/CancellationPolicyTemplatePage"),
  "CancellationPolicyTemplatePage",
);
const LateArrivalPolicyTemplatePage = lazyNamed(
  () => import("./pages/templates/LateArrivalPolicyTemplatePage"),
  "LateArrivalPolicyTemplatePage",
);
const PaymentReminderTemplatePage = lazyNamed(
  () => import("./pages/templates/PaymentReminderTemplatePage"),
  "PaymentReminderTemplatePage",
);
const PrivacyPage = lazy(() => import("../PrivacyPage.jsx"));
const TermsPage = lazy(() => import("../TermsPage.jsx"));

const deferred = (element) => <Suspense fallback={null}>{element}</Suspense>;
const newProfessionSlugs = professionCatalogue
  .filter((profession) => !establishedProfessionIds.has(profession.id))
  .map((profession) => profession.slug);

function LegacyBusinessProfessionRedirect() {
  const { professionKey } = useParams();
  const location = useLocation();
  const businessProfessionRoutes = {
    chiropodist: "chiropodists",
    podiatry: "chiropodists",
    "foot-care": "chiropodists",
    "podiatry-foot-care": "chiropodists",
  };
  const target = businessProfessionRoutes[professionKey];
  return <Navigate to={(target ? "/" + target : "/") + (location.search || "")} replace />;
}

// Business pages are served at root paths on www.offlico.com (and on
// localhost and previews); the internal /for-businesses/* addresses keep
// working. Customer addresses (/hub, /looking-for/..., /for-customers/...)
// belong to LocalPickr, whose website has its own repo since 25 Oct 2026
// (Rithik-bot/localpickr): SiteDomainBoundary sends them to localpickr.com
// before anything here renders.
export function SaasRouteTree() {
  const productMarketingPage = deferred(<OptimizedNewHubApp />);
  return (
    <Routes>
      <Route element={<SaasLayout />}>
        <Route path="/" element={productMarketingPage} />
        <Route path="/book-a-demo" element={<Navigate to="/contact" replace />} />
        <Route path="/for-businesses/*" element={productMarketingPage} />
        <Route path="/6-months-free" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={productMarketingPage} />
        <Route path="/features" element={productMarketingPage} />
        <Route path="/features/*" element={productMarketingPage} />
        <Route path="/contact" element={productMarketingPage} />
        <Route path="/switch" element={productMarketingPage} />
        <Route path="/all-mobile-businesses/*" element={productMarketingPage} />
        <Route path="/professions/*" element={productMarketingPage} />
        {[
          "chiropodists", "chiropodist", "mobile-hairdressers", "mobile-hairdresser",
          "cleaners", "cleaner", "gardeners", "gardener",
          "ear-wax-removal-specialists", "ear-wax-removal-specialist",
        ].map((profession) => (
          <Route key={profession} path={"/" + profession + "/*"} element={productMarketingPage} />
        ))}
        {newProfessionSlugs.map((profession) => (
          <Route key={profession} path={"/" + profession + "/*"} element={productMarketingPage} />
        ))}
        <Route path="/switch-to-offlico" element={<Navigate to="/switch" replace />} />
        {/* Comparison pages, retired 1 Nov 2026: Render sends these addresses to /switch/ too. */}
        {[
          "/comparison", "/compare", "/vs", "/alternatives",
          "/offlico-vs-fresha", "/offlico-vs-booksy", "/offlico-vs-acuity",
        ].map((path) => (
          <Route key={path} path={path} element={<Navigate to="/switch" replace />} />
        ))}
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/for" element={<Navigate to="/" replace />} />
        <Route path="/for/:professionKey" element={<LegacyBusinessProfessionRedirect />} />
        <Route path="/for/*" element={<Navigate to="/" replace />} />
        <Route element={deferred(<MarketingLayout />)}>
          <Route path="/blog" element={deferred(<BlogIndexPage />)} />
          <Route
            path="/blog/client-payment-tracking-small-business"
            element={<Navigate to="/blog/client-payment-tracker-small-business" replace />}
          />
          <Route path="/blog/:slug" element={deferred(<BlogArticlePage />)} />
          <Route path="/templates" element={deferred(<TemplatesPage />)} />
          <Route path="/templates/gdpr-document-checklist" element={deferred(<GdprDocumentChecklistPage />)} />
          <Route path="/templates/appointment-confirmation-template" element={deferred(<AppointmentConfirmationTemplatePage />)} />
          <Route path="/templates/cancellation-policy-template" element={deferred(<CancellationPolicyTemplatePage />)} />
          <Route path="/templates/late-arrival-policy-template" element={deferred(<LateArrivalPolicyTemplatePage />)} />
          <Route path="/templates/payment-reminder-template" element={deferred(<PaymentReminderTemplatePage />)} />
          <Route path="/for-businesses/tools" element={deferred(<ToolsHubPage />)} />
          <Route path="/tools" element={deferred(<ToolsHubPage />)} />
          <Route path="/for-businesses/tools/service-pricing-calculator" element={deferred(<ServicePricingCalculatorPage />)} />
          <Route path="/tools/service-pricing-calculator" element={deferred(<ServicePricingCalculatorPage />)} />
          <Route path="/for-businesses/tools/no-show-cost-calculator" element={deferred(<NoShowCostCalculatorPage />)} />
          <Route path="/tools/no-show-cost-calculator" element={deferred(<NoShowCostCalculatorPage />)} />
          <Route path="/for-businesses/tools/mileage-claim-calculator" element={deferred(<MileageClaimCalculatorPage />)} />
          <Route path="/tools/mileage-claim-calculator" element={deferred(<MileageClaimCalculatorPage />)} />
          <Route path="/for-businesses/tools/daily-route-capacity-calculator" element={deferred(<DailyRouteCapacityCalculatorPage />)} />
          <Route path="/tools/daily-route-capacity-calculator" element={deferred(<DailyRouteCapacityCalculatorPage />)} />
          <Route path="/for-businesses/tools/late-cancellation-policy-builder" element={deferred(<LateCancellationPolicyBuilderPage />)} />
          <Route path="/tools/late-cancellation-policy-builder" element={deferred(<LateCancellationPolicyBuilderPage />)} />
          <Route path="/for-businesses/tools/appointment-reminder-message-generator" element={deferred(<AppointmentReminderMessageGeneratorPage />)} />
          <Route path="/tools/appointment-reminder-message-generator" element={deferred(<AppointmentReminderMessageGeneratorPage />)} />
          <Route path="/what-is-offlico" element={deferred(<WhatIsOfflicoPage />)} />
        </Route>
        <Route path="/privacy" element={deferred(<PrivacyPage />)} />
        <Route path="/terms" element={deferred(<TermsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
